import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Upload, Users, Plus, X, Check, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RegisterParticipantDialogProps {
  classId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onParticipantRegistered?: () => void;
}

interface ParticipantData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

const RegisterParticipantDialog = ({ classId, open, onOpenChange, onParticipantRegistered }: RegisterParticipantDialogProps) => {
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(false);
  
  // Manual form state
  const [manualForm, setManualForm] = useState<ParticipantData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  // Import state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [textInput, setTextInput] = useState('');
  const [parsedParticipants, setParsedParticipants] = useState<ParticipantData[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const resetForm = () => {
    setManualForm({ first_name: '', last_name: '', email: '', phone: '' });
    setCsvFile(null);
    setTextInput('');
    setParsedParticipants([]);
    setValidationErrors([]);
    setShowPreview(false);
    setActiveTab('manual');
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateParticipant = (participant: ParticipantData, index: number): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (!participant.first_name || participant.first_name.trim().length < 2) {
      errors.push({
        row: index + 1,
        field: 'first_name',
        message: 'Nome deve ter pelo menos 2 caracteres'
      });
    }

    if (!participant.last_name || participant.last_name.trim().length < 2) {
      errors.push({
        row: index + 1,
        field: 'last_name',
        message: 'Sobrenome deve ter pelo menos 2 caracteres'
      });
    }

    if (!participant.email || !validateEmail(participant.email)) {
      errors.push({
        row: index + 1,
        field: 'email',
        message: 'Email inválido'
      });
    }

    return errors;
  };

  // Helper function to search for contact in customer_contacts table
  const searchCustomerContact = async (email: string) => {
    const { data, error } = await supabase
      .from('customer_contacts')
      .select('*')
      .ilike('email', email)
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 means no rows found
      throw error;
    }
    
    return data;
  };

  // Helper function to search contact in HubSpot
  const searchContactInHubSpot = async (email: string) => {
    try {
      console.log('🔍 Buscando contato no HubSpot:', email);
      const response = await supabase.functions.invoke('search-contact-hubspot', {
        body: { email }
      });

      console.log('📥 Resposta do HubSpot search:', response);

      if (response.error) {
        console.error('❌ Erro ao chamar search-contact-hubspot:', response.error);
        return null;
      }

      const result = response.data;
      if (result?.ok && result?.data?.results && result.data.results.length > 0) {
        console.log('✅ Contato encontrado no HubSpot:', result.data.results[0]);
        return result.data.results[0];
      }
      
      console.log('ℹ️ Contato não encontrado no HubSpot');
      return null;
    } catch (error) {
      console.error('❌ Erro inesperado ao buscar no HubSpot:', error);
      return null;
    }
  };

  // Helper function to create contact in HubSpot
  const createContactInHubSpot = async (contactData: {
    email: string;
    firstname?: string;
    lastname?: string;
    phone?: string;
  }) => {
    try {
      console.log('➕ Criando contato no HubSpot:', contactData);
      
      const upsertData = {
        contactId: "", // Empty for new contact
        companyId: "", // Could be set if we have company association
        firstname: contactData.firstname || "",
        lastname: contactData.lastname || "",
        email: contactData.email,
        jobtitle: "", // Could be added to form if needed
        mobilephone: contactData.phone || "",
        hs_persona: "" // Could be set based on business logic
      };

      const response = await supabase.functions.invoke('upsert-contact-hubspot', {
        body: upsertData
      });

      console.log('📥 Resposta da criação no HubSpot:', response);

      if (response.error) {
        console.error('❌ Erro ao chamar upsert-contact-hubspot:', response.error);
        throw new Error('Erro ao criar contato no HubSpot');
      }

      const result = response.data;
      if (result?.ok && result?.contactId) {
        console.log('✅ Contato criado no HubSpot com sucesso. Contact ID:', result.contactId);
        return result.contactId; // Return the HubSpot contact ID
      } else {
        console.error('❌ Falha na criação do contato no HubSpot:', result?.error);
        throw new Error(result?.error || 'Erro ao criar contato no HubSpot');
      }
    } catch (error) {
      console.error('❌ Erro inesperado ao criar no HubSpot:', error);
      throw error;
    }
  };

  // Helper function to create customer_contact record
  const createCustomerContact = async (contactData: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    job_title?: string;
  }, hubspotId?: string) => {
    console.log('📝 Criando customer_contact:', contactData, 'HubSpot ID:', hubspotId);
    
    // First, get the customer_id from the class subscription
    console.log('🔍 Buscando customer_id para a classe:', classId);
    const { data: classCustomer, error: classCustomerError } = await supabase
      .from('classes')
      .select(`
        subscription_id,
        subscriptions!inner(
          id,
          subscription_customers!inner(
            customer_id
          )
        )
      `)
      .eq('id', classId)
      .single();

    if (classCustomerError) {
      console.error('❌ Erro ao buscar customer_id:', classCustomerError);
      throw new Error('Erro ao obter customer_id da classe');
    }

    const customerId = classCustomer?.subscriptions?.subscription_customers?.[0]?.customer_id;
    if (!customerId) {
      console.error('❌ customer_id não encontrado para a classe');
      throw new Error('customer_id não encontrado para esta classe');
    }

    console.log('✅ customer_id encontrado:', customerId);
    
    // Insert customer contact
    const { data: customerContact, error: customerContactError } = await supabase
      .from('customer_contacts')
      .insert({
        customer_id: customerId,
        first_name: contactData.first_name,
        last_name: contactData.last_name,
        email: contactData.email,
        phone: contactData.phone || null,
        job_title: contactData.job_title || null,
        is_active: true
      })
      .select()
      .single();

    if (customerContactError) {
      console.error('❌ Erro ao criar customer_contact:', customerContactError);
      throw customerContactError;
    }

    console.log('✅ Customer contact criado:', customerContact);

    // Create external_ids record if we have a HubSpot ID
    if (hubspotId && customerContact) {
      console.log('🔗 Criando external_id para HubSpot:', hubspotId);
      const { error: externalIdError } = await supabase
        .from('external_ids')
        .insert({
          entity_name: 'customer_contacts',
          entity_id: customerContact.id,
          system_name: 'hubspot',
          external_id: hubspotId
        });

      if (externalIdError) {
        console.error('⚠️ Erro ao criar external_id:', externalIdError);
        // Don't throw here, as the main customer contact was created successfully
      } else {
        console.log('✅ External ID criado com sucesso');
      }
    }

    return customerContact;
  };

  // Main function to handle participant with HubSpot integration
  const processParticipantWithHubSpot = async (participantData: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  }) => {
    console.log('🎯 Processando participante com integração HubSpot:', participantData);
    
    let customerContactId = null;
    let isCustomerContact = false;

    try {
      // Step 1: Check if email exists in customer_contacts
      console.log('📋 Verificando customer_contacts...');
      const existingCustomerContact = await searchCustomerContact(participantData.email);
      
      if (existingCustomerContact) {
        console.log('✅ Contato encontrado em customer_contacts:', existingCustomerContact.id);
        customerContactId = existingCustomerContact.id;
        isCustomerContact = true;
      } else {
        console.log('ℹ️ Contato não encontrado em customer_contacts, buscando no HubSpot...');
        
        // Step 2: Search in HubSpot
        const hubspotContact = await searchContactInHubSpot(participantData.email);
        
        if (hubspotContact) {
          console.log('✅ Contato encontrado no HubSpot, criando customer_contact...');
          // Contact found in HubSpot - create customer_contact record
          const customerContact = await createCustomerContact({
            first_name: hubspotContact.properties.firstname || participantData.first_name,
            last_name: hubspotContact.properties.lastname || participantData.last_name,
            email: hubspotContact.properties.email || participantData.email,
            phone: hubspotContact.properties.mobilephone || hubspotContact.properties.phone || participantData.phone,
            job_title: hubspotContact.properties.jobtitle
          }, hubspotContact.id);
          
          customerContactId = customerContact.id;
          isCustomerContact = true;
        } else {
          console.log('ℹ️ Contato não encontrado no HubSpot, criando novo...');
          
          // Step 3: Create contact in HubSpot first, then in customer_contacts
          try {
            const newHubspotContact = await createContactInHubSpot({
              email: participantData.email,
              firstname: participantData.first_name,
              lastname: participantData.last_name,
              phone: participantData.phone
            });
            
            console.log('✅ Contato criado no HubSpot, criando customer_contact...');
            
            // Create customer_contact with HubSpot ID
            const customerContact = await createCustomerContact({
              first_name: participantData.first_name,
              last_name: participantData.last_name,
              email: participantData.email,
              phone: participantData.phone
            }, newHubspotContact);
            
            customerContactId = customerContact.id;
            isCustomerContact = true;
            
            console.log('🎉 Fluxo completo HubSpot concluído com sucesso!');
          } catch (hubspotError) {
            console.error('❌ Erro na criação no HubSpot:', hubspotError);
            // If HubSpot creation fails, we'll proceed without customer_contact_id
            toast.warning('Contato criado localmente, mas não foi possível sincronizar com HubSpot');
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro no processamento do participante:', error);
      toast.warning('Erro na integração HubSpot, participante será criado apenas localmente');
    }

    const result = {
      customer_contact_id: customerContactId,
      is_customer_contact: isCustomerContact
    };
    
    console.log('📊 Resultado do processamento:', result);
    return result;
  };

  const handleManualSubmit = async () => {
    const errors = validateParticipant(manualForm, 0);
    if (errors.length > 0) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    try {
      // Check if participant already exists
      const { data: existing } = await supabase
        .from('class_participants')
        .select('*')
        .eq('class_id', classId)
        .ilike('email', manualForm.email);

      if (existing && existing.length > 0) {
        toast.error('Participante já cadastrado nesta formação');
        return;
      }

      // Process participant with HubSpot integration
      console.log('🚀 Iniciando processamento com integração HubSpot...');
      const participantData = {
        first_name: manualForm.first_name.trim(),
        last_name: manualForm.last_name.trim(),
        email: manualForm.email,
        phone: manualForm.phone
      };

      const { customer_contact_id, is_customer_contact } = await processParticipantWithHubSpot(participantData);

      // Create participant with customer_contact_id if available
      console.log('👥 Criando participante na tabela class_participants...');
      const { error } = await supabase
        .from('class_participants')
        .insert({
          class_id: classId,
          customer_contact_id: customer_contact_id,
          first_name: manualForm.first_name.trim(),
          last_name: manualForm.last_name.trim(),
          email: manualForm.email,
          phone: manualForm.phone || null,
          is_customer_contact: is_customer_contact,
          status: 'registered',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Update the classes table with invitees_updated_at timestamp
      const { error: updateError } = await supabase
        .from('classes')
        .update({ 
          invitees_updated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', classId);

      if (updateError) {
        console.error('Error updating class invitees_updated_at:', updateError);
        // Don't throw error here as the participant was already added successfully
      }

      const successMessage = is_customer_contact 
        ? 'Participante cadastrado e vinculado ao HubSpot com sucesso!'
        : 'Participante cadastrado com sucesso!';
      
      console.log('🎉 Participante criado com sucesso!');
      toast.success(successMessage);
      resetForm();
      onOpenChange(false);
      onParticipantRegistered?.();
    } catch (error) {
      console.error('❌ Erro ao cadastrar participante:', error);
      toast.error('Erro ao cadastrar participante');
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (content: string): ParticipantData[] => {
    const lines = content.split('\n').filter(line => line.trim());
    const participants: ParticipantData[] = [];
    
    // Skip header if present
    const startIndex = lines[0]?.toLowerCase().includes('nome') || lines[0]?.toLowerCase().includes('email') ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const values = lines[i].split(',').map(val => val.trim().replace(/"/g, ''));
      if (values.length >= 3) {
        participants.push({
          first_name: values[0] || '',
          last_name: values[1] || '',
          email: values[2] || '',
          phone: values[3] || ''
        });
      }
    }
    
    return participants;
  };

  const parseTextInput = (text: string): ParticipantData[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const participants: ParticipantData[] = [];
    
    for (const line of lines) {
      const values = line.split(',').map(val => val.trim());
      if (values.length >= 3) {
        participants.push({
          first_name: values[0] || '',
          last_name: values[1] || '',
          email: values[2] || '',
          phone: values[3] || ''
        });
      }
    }
    
    return participants;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Por favor, selecione um arquivo CSV');
      return;
    }

    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const participants = parseCSV(content);
      setParsedParticipants(participants);
      
      // Validate all participants
      const allErrors: ValidationError[] = [];
      participants.forEach((participant, index) => {
        const errors = validateParticipant(participant, index);
        allErrors.push(...errors);
      });
      
      setValidationErrors(allErrors);
      setShowPreview(true);
    };
    
    reader.readAsText(file);
  };

  const handleTextImport = () => {
    if (!textInput.trim()) {
      toast.error('Por favor, cole os dados dos participantes');
      return;
    }

    const participants = parseTextInput(textInput);
    setParsedParticipants(participants);
    
    // Validate all participants
    const allErrors: ValidationError[] = [];
    participants.forEach((participant, index) => {
      const errors = validateParticipant(participant, index);
      allErrors.push(...errors);
    });
    
    setValidationErrors(allErrors);
    setShowPreview(true);
  };

  const handleBulkSubmit = async () => {
    if (validationErrors.length > 0) {
      toast.error('Por favor, corrija os erros antes de prosseguir');
      return;
    }

    setLoading(true);
    try {
      // Check for existing participants
      const emails = parsedParticipants.map(p => p.email);
      const { data: existing } = await supabase
        .from('class_participants')
        .select('email')
        .eq('class_id', classId)
        .in('email', emails);

      const existingEmails = existing?.map(e => e.email) || [];
      const newParticipants = parsedParticipants.filter(p => !existingEmails.includes(p.email));

      if (existingEmails.length > 0) {
        toast.warning(`${existingEmails.length} participante(s) já cadastrado(s) foram ignorado(s)`);
      }

      if (newParticipants.length === 0) {
        toast.error('Todos os participantes já estão cadastrados');
        return;
      }

      // Process each participant with HubSpot integration
      const participantsToInsert = [];
      let hubspotSyncedCount = 0;
      
      for (const participant of newParticipants) {
        try {
          const participantData = {
            first_name: participant.first_name.trim(),
            last_name: participant.last_name.trim(),
            email: participant.email,
            phone: participant.phone
          };

          const { customer_contact_id, is_customer_contact } = await processParticipantWithHubSpot(participantData);
          
          if (is_customer_contact) {
            hubspotSyncedCount++;
          }
          
          participantsToInsert.push({
            class_id: classId,
            customer_contact_id: customer_contact_id,
            first_name: participant.first_name.trim(),
            last_name: participant.last_name.trim(),
            email: participant.email,
            phone: participant.phone || null,
            is_customer_contact: is_customer_contact,
            status: 'registered',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        } catch (error) {
          console.error(`Error processing participant ${participant.email}:`, error);
          // Add participant without HubSpot integration
          participantsToInsert.push({
            class_id: classId,
            customer_contact_id: null,
            first_name: participant.first_name.trim(),
            last_name: participant.last_name.trim(),
            email: participant.email,
            phone: participant.phone || null,
            is_customer_contact: false,
            status: 'registered',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }
      }

      // Insert all participants at once
      const { error } = await supabase
        .from('class_participants')
        .insert(participantsToInsert);

      if (error) throw error;

      // Update the classes table with invitees_updated_at timestamp
      const { error: updateError } = await supabase
        .from('classes')
        .update({ 
          invitees_updated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', classId);

      if (updateError) {
        console.error('Error updating class invitees_updated_at:', updateError);
        // Don't throw error here as the participants were already added successfully
      }

      const baseMessage = `${newParticipants.length} participante(s) cadastrado(s) com sucesso!`;
      const hubspotMessage = hubspotSyncedCount > 0 
        ? ` ${hubspotSyncedCount} sincronizado(s) com HubSpot.`
        : '';
      
      toast.success(baseMessage + hubspotMessage);
      resetForm();
      onOpenChange(false);
      onParticipantRegistered?.();
    } catch (error) {
      console.error('Erro ao cadastrar participantes:', error);
      toast.error('Erro ao cadastrar participantes');
    } finally {
      setLoading(false);
    }
  };

  const removeParticipant = (index: number) => {
    const updated = parsedParticipants.filter((_, i) => i !== index);
    setParsedParticipants(updated);
    
    // Re-validate
    const allErrors: ValidationError[] = [];
    updated.forEach((participant, index) => {
      const errors = validateParticipant(participant, index);
      allErrors.push(...errors);
    });
    setValidationErrors(allErrors);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Cadastrar Participantes
          </DialogTitle>
          <DialogDescription>
            Adicione participantes manualmente ou importe de arquivo CSV/texto
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Manual
            </TabsTrigger>
            <TabsTrigger value="csv" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Arquivo CSV
            </TabsTrigger>
            <TabsTrigger value="text" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Colar Texto
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cadastro Manual</CardTitle>
                <CardDescription>
                  Preencha os dados do participante
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Nome *</Label>
                    <Input
                      id="first_name"
                      value={manualForm.first_name}
                      onChange={(e) => setManualForm(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="João"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Sobrenome *</Label>
                    <Input
                      id="last_name"
                      value={manualForm.last_name}
                      onChange={(e) => setManualForm(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Silva"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={manualForm.email}
                      onChange={(e) => setManualForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      value={manualForm.phone}
                      onChange={(e) => setManualForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleManualSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  'Cadastrar Participante'
                )}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="csv" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Importar Arquivo CSV</CardTitle>
                <CardDescription>
                  Selecione um arquivo CSV com os dados dos participantes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="csv-file">Arquivo CSV</Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Formato esperado: Nome, Sobrenome, Email, Telefone
                  </p>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Exemplo de formato CSV:</strong><br />
                    Nome,Sobrenome,Email,Telefone<br />
                    João,Silva,joao@empresa.com,(11) 99999-9999<br />
                    Maria,Santos,maria@empresa.com,(11) 88888-8888
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {showPreview && activeTab === 'csv' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    Preview dos Participantes
                    <Badge variant="secondary">{parsedParticipants.length} participante(s)</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {validationErrors.length > 0 && (
                    <Alert className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{validationErrors.length} erro(s) encontrado(s):</strong>
                        <ul className="mt-2 list-disc list-inside">
                          {validationErrors.slice(0, 5).map((error, index) => (
                            <li key={index}>
                              Linha {error.row}: {error.message}
                            </li>
                          ))}
                          {validationErrors.length > 5 && (
                            <li>+ {validationErrors.length - 5} outros erros...</li>
                          )}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {parsedParticipants.map((participant, index) => {
                      const hasError = validationErrors.some(e => e.row === index + 1);
                      return (
                        <div key={index} className={`flex items-center justify-between p-3 rounded border ${hasError ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                          <div className="flex-1">
                            <div className="font-extrabold">{participant.first_name} {participant.last_name}</div>
                            <div className="text-sm text-gray-500">{participant.email}</div>
                            {participant.phone && (
                              <div className="text-sm text-gray-500">{participant.phone}</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParticipant(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {showPreview && activeTab === 'csv' && (
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Voltar
                </Button>
                <Button 
                  onClick={handleBulkSubmit} 
                  disabled={loading || validationErrors.length > 0}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    `Cadastrar ${parsedParticipants.length} Participante(s)`
                  )}
                </Button>
              </DialogFooter>
            )}
          </TabsContent>

          <TabsContent value="text" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Colar Dados</CardTitle>
                <CardDescription>
                  Cole os dados dos participantes separados por vírgulas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="text-input">Dados dos Participantes</Label>
                  <Textarea
                    id="text-input"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="João,Silva,joao@empresa.com,(11) 99999-9999&#10;Maria,Santos,maria@empresa.com,(11) 88888-8888"
                    rows={6}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Uma linha por participante. Formato: Nome, Sobrenome, Email, Telefone
                  </p>
                </div>

                <Button onClick={handleTextImport} disabled={!textInput.trim()}>
                  <FileText className="h-4 w-4 mr-2" />
                  Processar Dados
                </Button>
              </CardContent>
            </Card>

            {showPreview && activeTab === 'text' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    Preview dos Participantes
                    <Badge variant="secondary">{parsedParticipants.length} participante(s)</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {validationErrors.length > 0 && (
                    <Alert className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{validationErrors.length} erro(s) encontrado(s):</strong>
                        <ul className="mt-2 list-disc list-inside">
                          {validationErrors.slice(0, 5).map((error, index) => (
                            <li key={index}>
                              Linha {error.row}: {error.message}
                            </li>
                          ))}
                          {validationErrors.length > 5 && (
                            <li>+ {validationErrors.length - 5} outros erros...</li>
                          )}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {parsedParticipants.map((participant, index) => {
                      const hasError = validationErrors.some(e => e.row === index + 1);
                      return (
                        <div key={index} className={`flex items-center justify-between p-3 rounded border ${hasError ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                          <div className="flex-1">
                            <div className="font-extrabold">{participant.first_name} {participant.last_name}</div>
                            <div className="text-sm text-gray-500">{participant.email}</div>
                            {participant.phone && (
                              <div className="text-sm text-gray-500">{participant.phone}</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParticipant(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {showPreview && activeTab === 'text' && (
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Voltar
                </Button>
                <Button 
                  onClick={handleBulkSubmit} 
                  disabled={loading || validationErrors.length > 0}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    `Cadastrar ${parsedParticipants.length} Participante(s)`
                  )}
                </Button>
              </DialogFooter>
            )}
          </TabsContent>
        </Tabs>

        {!showPreview && (activeTab === 'csv' || activeTab === 'text') && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegisterParticipantDialog;
